/**
 * @event/i18n — foundation-level localization helpers (browser-safe).
 *
 * The default entrypoint is deliberately zero-runtime-dep and free of Node
 * built-ins so that `apps/web` (via Vite) can consume the same types and
 * helpers the server uses. Node-only helpers (disk bundle loaders) live
 * under `@event/i18n/node` to keep the browser bundle clean.
 */

/** Locales supported by the platform during the bootstrap wave. */
export type LocaleCode = 'cs' | 'en';

/** Czech is the primary default; English is the required secondary locale. */
export const DEFAULT_LOCALE: LocaleCode = 'cs';

export const SUPPORTED_LOCALES: readonly LocaleCode[] = ['cs', 'en'] as const;

export type MessageBundle = Readonly<Record<string, string>>;

export type MessageCatalog = Readonly<Record<LocaleCode, MessageBundle>>;

export function isSupportedLocale(value: string | undefined | null): value is LocaleCode {
  return value != null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Negotiate a locale from a candidate value (query param, header, user
 * preference). Falls back to `DEFAULT_LOCALE` when nothing supported is
 * found.
 */
export function resolveLocale(
  candidate: string | undefined | null,
  supported: readonly LocaleCode[] = SUPPORTED_LOCALES,
): LocaleCode {
  if (candidate && (supported as readonly string[]).includes(candidate)) {
    return candidate as LocaleCode;
  }
  return DEFAULT_LOCALE;
}

export interface TranslateOptions {
  readonly locale?: string | null;
  readonly fallbackLocale?: LocaleCode;
  readonly values?: Readonly<Record<string, string | number>>;
  readonly catalog?: MessageCatalog;
}

/**
 * Look up a key in the catalog for the resolved locale, falling back to
 * the fallback locale and finally to the key itself. Supports `{name}`
 * interpolation; unknown placeholders are left intact so they surface
 * during review.
 *
 * The caller is responsible for supplying a `catalog`. If none is provided,
 * the key is returned verbatim — that way this package stays platform-
 * agnostic and never silently swallows missing translations.
 */
export function translate(key: string, options: TranslateOptions = {}): string {
  const catalog = options.catalog;
  if (!catalog) {
    return key;
  }
  const locale = resolveLocale(options.locale ?? undefined);
  const fallback = options.fallbackLocale ?? DEFAULT_LOCALE;
  const template = catalog[locale]?.[key] ?? catalog[fallback]?.[key];
  if (template === undefined) {
    return key;
  }
  return interpolate(template, options.values);
}

function interpolate(
  template: string,
  values: Readonly<Record<string, string | number>> = {},
): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Convenience factory for server and worker code that wants a simple
 * `t(key, values?)` function bound to a specific catalog + locale.
 */
export function createTranslator(
  catalog: MessageCatalog,
  locale: LocaleCode,
  fallbackLocale: LocaleCode = DEFAULT_LOCALE,
) {
  return (key: string, values?: Readonly<Record<string, string | number>>) =>
    translate(key, { catalog, locale, fallbackLocale, values });
}
